// generateConsentPdf.js
// Standalone 1-page Electronic Communication Consent Certificate
import jsPDF from 'jspdf';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Australia/Melbourne',
  }) + ' AEST';
}

function todayAU() {
  return new Date().toLocaleDateString('en-AU', {
    day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Australia/Melbourne',
  });
}

// ── Colours ───────────────────────────────────────────────────────────────────
const C = {
  brand:  [37, 99, 235],    // #2563EB blue
  dark:   [30, 41, 59],     // #1E293B slate-800
  mid:    [100, 116, 139],  // #64748B slate-500
  light:  [241, 245, 249],  // #F1F5F9 slate-100
  white:  [255, 255, 255],
  green:  [22, 163, 74],
  greenBg:[240, 253, 244],  // green-50
  border: [203, 213, 225],  // slate-300
};

// ── Main export ───────────────────────────────────────────────────────────────

export function generateConsentPdf({ property, tenancy, consentStatus, landlordName }) {
  if (!consentStatus?.consentActive) return; // only issue when consent is on file

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 24;
  const contentW = W - margin * 2;

  // ── Header band ─────────────────────────────────────────────────────────────
  doc.setFillColor(...C.brand);
  doc.rect(0, 0, W, 22, 'F');

  // Brand mark — small square
  doc.setFillColor(...C.white);
  doc.roundedRect(margin, 5, 10, 10, 1.5, 1.5, 'F');

  // House icon (simplified lines)
  doc.setDrawColor(...C.brand);
  doc.setLineWidth(0.8);
  // roof peak
  doc.line(margin + 5, 7, margin + 2.5, 9.5);
  doc.line(margin + 5, 7, margin + 7.5, 9.5);
  // walls
  doc.line(margin + 2.5, 9.5, margin + 2.5, 13.5);
  doc.line(margin + 7.5, 9.5, margin + 7.5, 13.5);
  doc.line(margin + 2.5, 13.5, margin + 7.5, 13.5);
  // door
  doc.setLineWidth(0.5);
  doc.line(margin + 4.5, 13.5, margin + 4.5, 11.5);
  doc.line(margin + 5.5, 13.5, margin + 5.5, 11.5);
  doc.line(margin + 4.5, 11.5, margin + 5.5, 11.5);

  // Brand name
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text('RentFriendly', margin + 13, 13);

  // "Consent Certificate" label on right
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(196, 219, 255);
  doc.text('Electronic Communication Consent Certificate', W - margin, 13, { align: 'right' });

  let y = 38;

  // ── Certificate title ────────────────────────────────────────────────────────
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('Consent Certificate', margin, y);
  y += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('Electronic Communication — Residential Tenancies Act 1997 (Vic)', margin, y);
  y += 3;

  // Divider
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.4);
  doc.line(margin, y, W - margin, y);
  y += 8;

  // ── Green confirmation banner ────────────────────────────────────────────────
  doc.setFillColor(...C.greenBg);
  doc.setDrawColor(...C.green);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y, contentW, 16, 2, 2, 'FD');

  // Tick circle
  doc.setFillColor(...C.green);
  doc.circle(margin + 10, y + 8, 4.5, 'F');
  doc.setDrawColor(...C.white);
  doc.setLineWidth(1);
  doc.line(margin + 8, y + 8, margin + 9.5, y + 9.8);
  doc.line(margin + 9.5, y + 9.8, margin + 12.2, y + 6.3);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.green);
  doc.text('Consent on file', margin + 17, y + 6.5);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(21, 128, 61); // green-700
  doc.text('The tenant has consented to receiving rental notices electronically.', margin + 17, y + 12.5);

  y += 25;

  // ── Details grid ─────────────────────────────────────────────────────────────
  const labelW = 52;
  const col2X = margin + labelW;

  function detailRow(label, value, italic = false) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.mid);
    doc.text(label, margin, y);

    doc.setFontSize(9.5);
    doc.setFont('helvetica', italic ? 'italic' : 'normal');
    doc.setTextColor(...C.dark);
    doc.text(String(value || '—'), col2X, y);
    y += 8;
  }

  function sectionHeading(title) {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.brand);
    doc.text(title.toUpperCase(), margin, y);
    y += 1.5;
    doc.setDrawColor(...C.brand);
    doc.setLineWidth(0.25);
    doc.line(margin, y, W - margin, y);
    y += 6.5;
  }

  // Tenant details
  sectionHeading('Tenant');
  detailRow('Full name', tenancy.tenant_name);
  detailRow('Email address', tenancy.tenant_email);

  y += 2;

  // Property
  sectionHeading('Rental property');
  detailRow('Address', property.address);

  y += 2;

  // Consent details
  sectionHeading('Consent details');

  const rawDate = consentStatus.consentDate?.split('T')[0] || consentStatus.consentDate;
  const consentDateDisplay = fmtDate(rawDate);
  detailRow('Date of consent', consentDateDisplay);

  const methodMap = {
    invite_flow:        'Tenant portal (online) — RentFriendly invite flow',
    landlord_recorded:  'Recorded by landlord from offline consent',
  };
  const methodDisplay = methodMap[consentStatus.consentMethod] || consentStatus.consentMethod || '—';
  detailRow('How obtained', methodDisplay);

  if (consentStatus.consentIpMasked) {
    detailRow('IP address', consentStatus.consentIpMasked + ' (masked for privacy)');
  }

  y += 2;

  // Landlord / issuer
  sectionHeading('Issued by');
  detailRow('Rental provider', landlordName || '—');
  detailRow('Date issued', todayAU());

  y += 4;

  // ── Legal statement ──────────────────────────────────────────────────────────
  doc.setFillColor(...C.light);
  doc.roundedRect(margin, y, contentW, 46, 2, 2, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.dark);
  doc.text('Legal statement', margin + 5, y + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  const legal1 = `This certificate confirms that the above-named tenant has given their informed consent to receive formal ` +
    `notices and documents from their rental provider electronically, pursuant to the Residential Tenancies Act 1997 (Vic).`;
  const legal2 = `The tenant was informed that this consent is voluntary, may be withdrawn at any time, and that withdrawal ` +
    `requires the rental provider to revert to delivering notices by hand or post.`;
  const legal3 = `This record was generated by RentFriendly and may be used as evidence of consent in VCAT proceedings or ` +
    `any formal dispute regarding the electronic service of notices.`;

  doc.text(legal1, margin + 5, y + 14, { maxWidth: contentW - 10 });
  doc.text(legal2, margin + 5, y + 25, { maxWidth: contentW - 10 });
  doc.text(legal3, margin + 5, y + 36, { maxWidth: contentW - 10 });

  y += 55;

  // ── Signature block ──────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('Authorised by:', margin, y);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.4);
  doc.line(margin + 28, y, margin + 100, y);

  y += 8;
  doc.text(`Name: ${landlordName || ''}`, margin, y);

  y += 8;
  doc.text('Date:', margin, y);
  doc.line(margin + 12, y, margin + 80, y);

  // ── Footer ───────────────────────────────────────────────────────────────────
  const footerY = H - 12;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY - 4, W - margin, footerY - 4);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.mid);
  doc.text('Generated by RentFriendly — rentfriendly.com.au', margin, footerY);
  doc.text(todayAU(), W - margin, footerY, { align: 'right' });

  // ── Save ─────────────────────────────────────────────────────────────────────
  const safeAddr = property.address.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-');
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`RentFriendly-Consent-Certificate-${safeAddr}-${dateStr}.pdf`);
}
